/**
 * Vorschau Einzel- / Team-Boni je Mitarbeiter (HJ1 + HJ2).
 * Einzel-Boni = dieselbe Logik wie LeistungPage (Leistungslohn).
 * Nur Anzeige — keine Buchung in Personaldaten/Kalkulation.
 */

import { isLegacyLockedMetrics } from '../slices/coreDataSlice';
import { computeTeamBonusShares, normalizeTeamBonus } from './teamBonusSelectors';

const AZUBI_ROLES = new Set(['Azubi', 'Prod. Azubi', 'produktiver Azubi']);
const COUNT_HJ = 6;

const safeDiv = (a: number, b: number) => (b ? a / b : 0);
/** Anzeige: negative Beträge → 0 */
const floorBonus = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

export interface StaffBonusPreviewRow {
    id: string;
    name: string;
    groupLabel: string;
    sharePercent: number;
    /** Auszahlung p. Monat 1. HJ (wie LeistungPage: max(0, BonusGesamt/6)) */
    bonusMonatHj1: number;
    bonusMonatHj2: number | null;
    /** Roh-Summe Halbjahr vor Floor (intern / Debug) */
    bonusHj1: number;
    bonusHj2: number | null;
}

export interface StaffBonusPreviewResult {
    mode: 'team' | 'individual';
    isHJ2Freigegeben: boolean;
    poolAmount: number;
    sumSelectedGrundgehalt: number;
    selectedCount: number;
    rows: StaffBonusPreviewRow[];
    sumMonatHj1: number;
    sumMonatHj2: number | null;
    sumHj1: number;
    sumHj2: number | null;
}

type BonusFaktoren = { bedienung: number; vk: number };

type BasisMetrics = {
    monatsStunden: number;
    tagesArbeitszeit: number;
    sollUmsatzMonat: number;
    sollUmsatzProStunde: number;
};

/**
 * Exakt wie LeistungPage.processHalbjahr → summary.bonusGesamt
 * (inkl. festgemachte Monate mit Snapshot).
 */
const sumLeistungBonusForHj = (
    hjData: any[] | undefined,
    liveBasis: BasisMetrics,
    liveFaktoren: BonusFaktoren,
    mwstFaktor: number,
    lockedMetricsIndex: any,
    halbjahrKey: 'halbjahr1' | 'halbjahr2'
): number => {
    if (!Array.isArray(hjData) || hjData.length === 0) return 0;

    const legacySnapshot = isLegacyLockedMetrics(lockedMetricsIndex) ? lockedMetricsIndex.metrics : null;
    const legacyBasis = legacySnapshot
        ? (halbjahrKey === 'halbjahr1' ? legacySnapshot.metricsHJ1 : legacySnapshot.metricsHJ2)
        : undefined;
    const legacyFaktoren = legacySnapshot
        ? (halbjahrKey === 'halbjahr1' ? legacySnapshot.faktorenHJ1 : legacySnapshot.faktorenHJ2)
        : undefined;
    const monthLockedIndex =
        !isLegacyLockedMetrics(lockedMetricsIndex) && lockedMetricsIndex
            ? lockedMetricsIndex[halbjahrKey]
            : undefined;

    let summeIstUmsatz = 0;
    let anzahlMonateMitUmsatz = 0;
    hjData.forEach((m) => {
        const bedien = Number(m?.bedienUmsatz) || 0;
        const vk = Number(m?.vkUmsatz) || 0;
        if (bedien + vk > 0) {
            summeIstUmsatz += bedien + vk;
            anzahlMonateMitUmsatz++;
        }
    });
    const avgIstUmsatzMonat = anzahlMonateMitUmsatz > 0 ? summeIstUmsatz / anzahlMonateMitUmsatz : 0;

    let bonusSum = 0;
    hjData.forEach((monat, index) => {
        const safeMonat = monat || {};
        const bedien = Number(safeMonat.bedienUmsatz) || 0;
        const vk = Number(safeMonat.vkUmsatz) || 0;
        const hasUmsatz = bedien + vk > 0;
        if (!hasUmsatz) return;

        const isLocked = Boolean(safeMonat.festmachen);
        const monthSnapshot = monthLockedIndex?.[String(index)];
        let basis: BasisMetrics = liveBasis;
        let faktoren: BonusFaktoren = liveFaktoren;

        if (isLocked) {
            if (monthSnapshot?.basisMetrics) {
                basis = {
                    monatsStunden: Number(monthSnapshot.basisMetrics.monatsStunden) || liveBasis.monatsStunden,
                    tagesArbeitszeit:
                        Number(monthSnapshot.basisMetrics.tagesArbeitszeit) || liveBasis.tagesArbeitszeit,
                    sollUmsatzMonat:
                        Number(monthSnapshot.basisMetrics.sollUmsatzMonat) || liveBasis.sollUmsatzMonat,
                    sollUmsatzProStunde:
                        Number(monthSnapshot.basisMetrics.sollUmsatzProStunde) ||
                        liveBasis.sollUmsatzProStunde,
                };
                if (monthSnapshot.bonusFaktoren) {
                    faktoren = {
                        bedienung: Number(monthSnapshot.bonusFaktoren.bedienung) || liveFaktoren.bedienung,
                        vk: Number(monthSnapshot.bonusFaktoren.vk) || liveFaktoren.vk,
                    };
                }
            } else if (legacyBasis) {
                basis = {
                    monatsStunden: Number(legacyBasis.monatsStunden) || liveBasis.monatsStunden,
                    tagesArbeitszeit: Number(legacyBasis.tagesArbeitszeit) || liveBasis.tagesArbeitszeit,
                    sollUmsatzMonat: Number(legacyBasis.sollUmsatzMonat) || liveBasis.sollUmsatzMonat,
                    sollUmsatzProStunde:
                        Number(legacyBasis.sollUmsatzProStunde) || liveBasis.sollUmsatzProStunde,
                };
                if (legacyFaktoren) {
                    faktoren = {
                        bedienung: Number(legacyFaktoren.bedienung) || liveFaktoren.bedienung,
                        vk: Number(legacyFaktoren.vk) || liveFaktoren.vk,
                    };
                }
            }
        }

        const avgIstUmsatzProStunde = safeDiv(avgIstUmsatzMonat, basis.monatsStunden);
        const effectiveUrlaubsTage = Number(safeMonat.urlaubsTage) || 0;
        const urlaubsAusgleich = avgIstUmsatzProStunde * effectiveUrlaubsTage * basis.tagesArbeitszeit;
        const gesamtUmsatzBrutto = bedien + vk + urlaubsAusgleich;

        const unprodStunden = Number(safeMonat.unproduktiveStunden) || 0;
        let angepassterSollUmsatz = basis.sollUmsatzMonat;
        if (!safeMonat.ueberstundenAuszahlen) {
            angepassterSollUmsatz -= unprodStunden * basis.sollUmsatzProStunde;
        }

        const deltaBrutto = gesamtUmsatzBrutto - angepassterSollUmsatz;
        const deltaNetto = deltaBrutto / (mwstFaktor || 1);
        const vkUmsatzNetto = vk / (mwstFaktor || 1);
        const bonusBedienung = deltaNetto * faktoren.bedienung;
        const bonusVK = deltaBrutto > 0 ? vkUmsatzNetto * faktoren.vk : 0;
        // Wie LeistungPage: Rohwerte inkl. Minus aufsummieren (Floor erst bei Monats-Auszahlung)
        bonusSum += bonusBedienung + bonusVK;
    });

    return bonusSum;
};

const buildLiveBasis = (
    maData: any,
    leistung: any,
    lohnfaktorAktiv: number,
    geraeteListe: any[],
    wochensatz: number
): BasisMetrics | null => {
    const wochenstunden = Number(maData.wochenstunden) || 0;
    const arbeitstage = Number(maData.arbeitstage) || 5;
    if (!wochenstunden || !arbeitstage) return null;

    const monatsStunden = wochenstunden * wochensatz;
    const tagesArbeitszeit = safeDiv(wochenstunden, arbeitstage);
    const effektiverBruttolohn =
        (Number(maData.grundgehalt) || 0) +
        (Number(maData.lohnkostenzusatz) || Number(maData.lohnzusatz) || 0);

    let currentLohnfaktor = lohnfaktorAktiv;
    const safeDeviceIds = Array.isArray(leistung?.selectedDeviceIds) ? leistung.selectedDeviceIds : [];
    if (maData.arbeitetMitGeraeten && safeDeviceIds.length > 0) {
        let totalGeraeteProzent = 0;
        safeDeviceIds.forEach((devId: string) => {
            const geraet = geraeteListe.find((g: any) => g && g.id === devId);
            if (geraet) totalGeraeteProzent += Number(geraet.kostenProzent) || 0;
        });
        currentLohnfaktor += (totalGeraeteProzent / 100) * 10;
    }

    const sollUmsatzMonat = effektiverBruttolohn * currentLohnfaktor;
    return {
        monatsStunden,
        tagesArbeitszeit,
        sollUmsatzMonat,
        sollUmsatzProStunde: safeDiv(sollUmsatzMonat, monatsStunden),
    };
};

/**
 * Einheitliche Bonus-Vorschau für Cockpit, Gesamtleistung und Änderungszentrale.
 */
export const computeStaffBonusPreview = (
    clientData: any,
    calcExtras?: {
        mwstSatz?: number;
        lohnfaktorAktiv?: number;
        geraeteListe?: any[];
        wochensatz?: number;
        lohnfaktorData?: any;
        isVatExempt?: boolean;
    }
): StaffBonusPreviewResult => {
    const cd = clientData || {};
    const config = normalizeTeamBonus(cd.teamBonus);
    const isHJ2Freigegeben = Boolean(cd.isHJ2Freigegeben);
    const empty = (mode: 'team' | 'individual'): StaffBonusPreviewResult => ({
        mode,
        isHJ2Freigegeben,
        poolAmount: config.poolAmount,
        sumSelectedGrundgehalt: 0,
        selectedCount: 0,
        rows: [],
        sumMonatHj1: 0,
        sumMonatHj2: isHJ2Freigegeben ? 0 : null,
        sumHj1: 0,
        sumHj2: isHJ2Freigegeben ? 0 : null,
    });

    if (config.bonusMode === 'team') {
        const computed = computeTeamBonusShares(cd);
        const selectedRows = computed.rows.filter((r) => r.selected);
        const rows: StaffBonusPreviewRow[] = selectedRows.map((r) => {
            const mon = floorBonus(r.calculatedBonusMonthly || 0);
            const hj = floorBonus(r.calculatedBonus || mon * COUNT_HJ);
            return {
                id: r.id,
                name: r.name,
                groupLabel: r.sourceLabel,
                sharePercent: r.sharePercent,
                bonusMonatHj1: mon,
                bonusMonatHj2: isHJ2Freigegeben ? mon : null,
                bonusHj1: hj,
                bonusHj2: isHJ2Freigegeben ? hj : null,
            };
        });
        const sumMonat = rows.reduce((s, r) => s + r.bonusMonatHj1, 0);
        const sumHj = rows.reduce((s, r) => s + r.bonusHj1, 0);
        return {
            mode: 'team',
            isHJ2Freigegeben,
            poolAmount: config.poolAmount,
            sumSelectedGrundgehalt: computed.sumSelectedGrundgehalt,
            selectedCount: rows.length,
            rows,
            sumMonatHj1: sumMonat,
            sumMonatHj2: isHJ2Freigegeben ? sumMonat : null,
            sumHj1: sumHj,
            sumHj2: isHJ2Freigegeben ? sumHj : null,
        };
    }

    const isVatExempt = Boolean(calcExtras?.isVatExempt ?? cd.isVatExempt ?? cd.umsatzsteuerbefreit);
    const mwstFaktor = isVatExempt ? 1 : 1 + ((calcExtras?.mwstSatz ?? 19) / 100 || 0);
    const wochensatz = calcExtras?.wochensatz || 4.33;
    const lohnfaktorAktiv = calcExtras?.lohnfaktorAktiv || 0;
    const geraeteListe = Array.isArray(calcExtras?.geraeteListe) ? calcExtras!.geraeteListe! : [];
    const mitarbeiterLeistung = cd.mitarbeiterLeistung || {};
    const leistungsprovision = cd.leistungsprovision || {};
    const faktorenHJ1: BonusFaktoren = {
        bedienung: (leistungsprovision.erstesHalbjahr?.provisionDienstleistung ?? 10) / 100,
        vk: (leistungsprovision.erstesHalbjahr?.provisionVerkauf ?? 10) / 100,
    };
    const faktorenHJ2: BonusFaktoren = {
        bedienung: (leistungsprovision.zweitesHalbjahr?.provisionDienstleistung ?? 10) / 100,
        vk: (leistungsprovision.zweitesHalbjahr?.provisionVerkauf ?? 10) / 100,
    };

    const rows: StaffBonusPreviewRow[] = [];
    (cd.personalData || []).forEach((maData: any) => {
        if (!maData?.id || AZUBI_ROLES.has(maData.taetigkeit)) return;
        const raw = mitarbeiterLeistung[maData.id];
        if (!raw) return;

        const liveBasis = buildLiveBasis(maData, raw, lohnfaktorAktiv, geraeteListe, wochensatz);
        if (!liveBasis) return;

        const lockedMetricsIndex = raw.lockedMetrics;
        const hj1Sum = sumLeistungBonusForHj(
            raw.halbjahr1,
            liveBasis,
            faktorenHJ1,
            mwstFaktor,
            lockedMetricsIndex,
            'halbjahr1'
        );
        const hj2Sum = isHJ2Freigegeben
            ? sumLeistungBonusForHj(
                  raw.halbjahr2,
                  liveBasis,
                  faktorenHJ2,
                  mwstFaktor,
                  lockedMetricsIndex,
                  'halbjahr2'
              )
            : 0;

        // Wie LeistungPage Auszahlung p. Monat: nur wenn Summe > 0
        const bonusMonatHj1 = floorBonus(safeDiv(hj1Sum, COUNT_HJ));
        const bonusMonatHj2 = isHJ2Freigegeben ? floorBonus(safeDiv(hj2Sum, COUNT_HJ)) : null;

        if (bonusMonatHj1 === 0 && (bonusMonatHj2 == null || bonusMonatHj2 === 0) && hj1Sum === 0 && hj2Sum === 0) {
            // trotzdem zeigen wenn Leistungsdaten existieren? Nur wenn irgendein Umsatzmonat da war
            const hasAny =
                (Array.isArray(raw.halbjahr1) &&
                    raw.halbjahr1.some(
                        (m: any) => (Number(m?.bedienUmsatz) || 0) + (Number(m?.vkUmsatz) || 0) > 0
                    )) ||
                (isHJ2Freigegeben &&
                    Array.isArray(raw.halbjahr2) &&
                    raw.halbjahr2.some(
                        (m: any) => (Number(m?.bedienUmsatz) || 0) + (Number(m?.vkUmsatz) || 0) > 0
                    ));
            if (!hasAny) return;
        }

        rows.push({
            id: String(maData.id),
            name: String(maData.name || 'Ohne Name'),
            groupLabel: 'Produktiv',
            sharePercent: 0,
            bonusMonatHj1,
            bonusMonatHj2,
            bonusHj1: floorBonus(hj1Sum),
            bonusHj2: isHJ2Freigegeben ? floorBonus(hj2Sum) : null,
        });
    });

    if (rows.length === 0) return empty('individual');

    return {
        mode: 'individual',
        isHJ2Freigegeben,
        poolAmount: 0,
        sumSelectedGrundgehalt: 0,
        selectedCount: rows.length,
        rows,
        sumMonatHj1: rows.reduce((s, r) => s + r.bonusMonatHj1, 0),
        sumMonatHj2: isHJ2Freigegeben
            ? rows.reduce((s, r) => s + (r.bonusMonatHj2 || 0), 0)
            : null,
        sumHj1: rows.reduce((s, r) => s + r.bonusHj1, 0),
        sumHj2: isHJ2Freigegeben ? rows.reduce((s, r) => s + (r.bonusHj2 || 0), 0) : null,
    };
};
